import { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import {
  getCache,
  setCache,
  getEnrichmentCacheKey,
  ENRICHMENT_CACHE_TTL,
} from "../lib/calendarCacheService";
import { CalendarEvent, PreCallSetter } from "../types/calendar";

// =============================================================================
// useCalendarEnrichment - CALENDAR EVENT ENRICHMENT (chrono-greedy queue, v3)
// =============================================================================
// PURPOSE: Enriches calendar events with pre-call submission data and outcome
// data. Outcome binding is keyed by chronological position within each lead's
// event stream, NOT by the closer-typed `callStartTime` field on the outcome.
//
// WHY: Closers regularly do impromptu off-calendar calls (phone, DM, Zoom one-off)
// and post-call them with a date that's correct for the actual conversation but
// disagrees with whichever calendar slot is sitting on the page waiting for an
// outcome. Date-field matching punishes proactive closers with admin (cancel
// the leftover slot). Position-by-time absorbs it: an outcome retires the
// oldest unmatched booking, regardless of what date the closer typed.
//
// DOCTRINE PRESERVED: a rebook after an outcome adds a new slot to the queue
// AFTER the outcome already retired the previous one — so the new slot stays
// flagged until its own outcome lands. Coverage = N outcomes cover N bookings.
//
// PORTED: computeOutcomeBindings is mirrored in the /cleanup-outcomes skill
// (altara/.claude/skills/cleanup-outcomes/scope_worklist.py). If you change
// the binding algorithm here, re-sync that port.
// =============================================================================

interface OutcomeRecord {
  status: string;
  cash: number | null;
  eventDate: string;
  callStartTime: string | null;
  /** Present ONLY while status === 'pending_close' — the open Pending Close
   *  bound to this booking. txnId is the SCO transaction id (the
   *  convert_pending_close target), NOT the booking id. */
  openPending?: { txnId: string; expectedAmount: number; createdAt: string };
}

// v3 cache shape (May 8 2026): bindings Map<transaction_id, OutcomeRecord>,
// not the prior Map<lead_id, OutcomeRecord[]>. Old v2 entries deserialize to
// `cached.bindings === undefined` → falsy guard re-fetches.
interface CachedEnrichment {
  preCallLeadIds: string[];
  bindings: Array<[string, OutcomeRecord]>;
  // The size of the lead set these bindings were computed from. The cache key
  // is range-based, but the lead set GROWS within a fixed range as the events
  // fetch paginates in. A fetch that fires on a partial events array (e.g. 255
  // of 738 leads mid-load) would otherwise poison the cache for the full-load
  // read: the range key hits, the stale partial bindings are served, and every
  // booking whose lead arrived in a later page shows a false "Missing Outcome"
  // (May 2026 grid: 285 false vs 49 real). Stamping the lead count lets the
  // full-load read reject a partial-load entry and recompute. May 2026 fix.
  leadCount: number;
  // Pre-call setters/dialers for the Setters & Dialers sidebar filter. Two maps:
  // `preCallSetterIdsByLead` (lead_id → distinct setter ids; stamps events) and
  // `preCallSetterNames` (setter id → full_name; builds the distinct checkbox
  // list). Absent on pre-v4 entries → `?? []` yields empty maps; the enrich_v4_
  // cache-prefix bump evicts them anyway (this is why the prefix was bumped v3→v4).
  preCallSetterIdsByLead?: Array<[string, string[]]>;
  preCallSetterNames?: Array<[string, string]>;
}

interface EnrichmentResult {
  enrichedEvents: CalendarEvent[];
  missingPreCalls: CalendarEvent[];
  missingOutcomes: CalendarEvent[];
  /** Lead-level set of lead_ids that have a sales_pre_call in the loaded range.
   *  Exposed so callers (e.g. flake-reschedule detection) can answer "did this
   *  lead have a pre-call" without enriching has_pre_call onto cancelled events. */
  preCallLeadIds: Set<string>;
  /** Distinct setters/dialers who filed a pre-call in range, sorted by name.
   *  The checkbox list for the Setters & Dialers sidebar filter. */
  preCallSetters: PreCallSetter[];
  loading: boolean;
  error: string | null;
}

interface RawTxnRow {
  id: string;
  lead_id: string;
  event_type: string;
  event_date: string;
  call_id: string | null;
  status: string | null;
  cash_collected: number | null;
  call_start_time: string | null;
  // Optional so existing unit-test fixtures stay valid; the live select always
  // returns both (needed for openPending on pending_close rows).
  custom_data?: Record<string, unknown> | null;
  created_at?: string;
}

/**
 * Outcome statuses that terminate a lead's lifecycle. When a terminal outcome
 * lands, every still-unmatched booking in the lead's queue is bound to it (the
 * lead is done — leftover calendar slots are noise the closer shouldn't have
 * to also cancel). Non-terminal outcomes (no_show, rescheduled, unique, etc.)
 * still pop one slot at a time because the lead is alive and each future call
 * needs its own outcome.
 *
 * `abandoned_payment_plan` is treated as terminal — lead stopped paying, the
 * lifecycle is in dispute/recovery, and any leftover calendar slots are not
 * actionable for the closer in the standard sense. `restored_from_abandon`
 * is non-terminal (lead is back in the pipeline).
 *
 * `pending_close` is DELIBERATELY NOT in this set (Jun 2026). As a
 * non-terminal it binds the oldest open booking — clearing that booking's
 * Missing-Outcome flag — without draining the lead's queue, with ZERO
 * algorithm change. A second booking for the same lead stays open until its
 * own outcome lands. Do not "fix" this by adding pending_close here.
 *
 * BUT a pending is a PLACEHOLDER, not a resolution: a later terminal outcome
 * for the same lead SUPERSEDES the pending binding (see `pendingBookingsByLead`
 * below). Without that, when a pending_close already popped the lead's only
 * booking, the later terminal outcome hits an empty queue and binds nothing —
 * leaving the lead stuck on ⏳ Pending after it paid/lost (Khairun Bubu, Jun
 * 2026: paid_in_full filed 21min after a pending_close, ⏳ never cleared).
 */
const TERMINAL_OUTCOME_STATUSES = new Set([
  "paid_in_full",
  "closed_payment_plan",
  "lost",
  "abandoned_payment_plan",
]);

/**
 * The status a resolved `pending_close` row is rewritten to (two-row
 * supersession model, S79). It is deliberately a label no count site matches,
 * so every aggregate drops it automatically. The calendar must drop it too —
 * see the skip in `computeOutcomeBindings`. Do NOT add it to
 * `TERMINAL_OUTCOME_STATUSES`: that would make it DRAIN the lead's queue and
 * bind every open booking to a $0 dead-label record — worse than the bug.
 */
const SUPERSEDED_OUTCOME_STATUS = "pending_superseded";

/**
 * Chrono-greedy queue with terminal-drain. Walk each lead's call_booked /
 * call_cancelled / sales_call_outcome rows in event_date order:
 *   - call_booked       → push { txnId, callId } onto queue
 *   - call_cancelled    → remove matching call_id from queue (or oldest)
 *   - sales_call_outcome (terminal)     → bind ALL queue entries to this
 *                                         outcome, then queue = []
 *   - sales_call_outcome (non-terminal) → bind oldest queue entry only
 *                                         (queue.shift)
 *
 * Doctrine preserved: a NEW booking arriving AFTER a terminal outcome enters
 * the queue fresh and stays unmatched until its own outcome arrives — so
 * "rebook reopens lead" still flags correctly.
 *
 * Returns Map<call_booked.transaction_id, OutcomeRecord>. Calendar events not
 * in the map are uncovered → flagged as missing outcome (if the call is past).
 */
export function computeOutcomeBindings(rows: RawTxnRow[]): Map<string, OutcomeRecord> {
  const bindings = new Map<string, OutcomeRecord>();
  const queues = new Map<
    string,
    Array<{ txnId: string; callId: string | null; callStartTime: string | null }>
  >();
  // Booking txnIds currently held by a non-final pending_close, keyed by lead.
  // A later terminal outcome for the same lead supersedes these (a pending is a
  // placeholder, not a resolution) — see the sales_call_outcome branch below.
  const pendingBookingsByLead = new Map<string, Set<string>>();

  const sorted = [...rows].sort((a, b) => {
    const t = a.event_date.localeCompare(b.event_date);
    if (t !== 0) return t;
    return a.id.localeCompare(b.id);
  });

  for (const row of sorted) {
    if (!row.lead_id) continue;
    const queue = queues.get(row.lead_id) ?? [];
    if (row.event_type === "call_booked") {
      queue.push({ txnId: row.id, callId: row.call_id, callStartTime: row.call_start_time });
    } else if (row.event_type === "call_cancelled") {
      // call_id is authoritative when present. ~1% of cancels arrive with
      // call_id NULL (Calendly webhook quirk on certain reschedule flows);
      // in those rows call_start_time IS set, so use it as the secondary
      // match key. Falling back to "remove oldest" was the May 22 Janan
      // Daniel false-positive: a NULL-call_id cancel ate an unrelated
      // older booking and orphaned it, so its later outcome bound to a
      // sibling instead. Verified 25/25 NULL-call_id cancels in Rayston
      // have call_start_time set — no need for a third "oldest" fallback.
      let idx = -1;
      if (row.call_id) {
        idx = queue.findIndex((q) => q.callId === row.call_id);
      } else if (row.call_start_time) {
        idx = queue.findIndex((q) => q.callStartTime === row.call_start_time);
      }
      if (idx >= 0) queue.splice(idx, 1);
    } else if (row.event_type === "sales_call_outcome") {
      if (!row.status) {
        queues.set(row.lead_id, queue);
        continue;
      }
      // `pending_superseded` is a DEAD LABEL, not an outcome. Under the
      // two-row supersession model (S79) a resolved pending keeps its own row
      // with status rewritten to this, and the REAL result lands as its own
      // terminal SCO row. So a superseded pending must never bind and never
      // consume a booking — if it does, it `queue.shift()`s the slot the
      // terminal outcome needs, the terminal then drains an empty queue, and
      // the chip freezes on "Pending Superseded" forever. It also can't be
      // rescued by `pendingBookingsByLead` below, because that set is keyed on
      // the literal string "pending_close" the rename replaced. Live Jul 2026:
      // 25/25 Rayston bookings mis-labeled, $93k of closed cash hidden.
      if (row.status === SUPERSEDED_OUTCOME_STATUS) {
        queues.set(row.lead_id, queue);
        continue;
      }
      const record: OutcomeRecord = {
        status: row.status,
        cash: row.cash_collected,
        eventDate: row.event_date,
        callStartTime: row.call_start_time,
        ...(row.status === "pending_close"
          ? {
              openPending: {
                txnId: row.id,
                expectedAmount: Number(row.custom_data?.expected_amount ?? 0),
                createdAt: row.created_at ?? row.event_date,
              },
            }
          : {}),
      };
      if (TERMINAL_OUTCOME_STATUSES.has(row.status)) {
        // Drain: every leftover booking gets bound to this terminal outcome.
        for (const entry of queue) bindings.set(entry.txnId, record);
        queue.length = 0;
        // Supersede any open pending_close placeholder bindings for this lead:
        // the closer filed (or converted to) a real terminal outcome, so a
        // booking still showing ⏳ Pending must flip to the terminal result.
        const heldPending = pendingBookingsByLead.get(row.lead_id);
        if (heldPending) {
          for (const txnId of heldPending) bindings.set(txnId, record);
          heldPending.clear();
        }
      } else {
        const target = queue.shift();
        if (target) {
          bindings.set(target.txnId, record);
          if (row.status === "pending_close") {
            // Remember this booking is held by a pending placeholder so a
            // later terminal outcome (above) can supersede it.
            let held = pendingBookingsByLead.get(row.lead_id);
            if (!held) {
              held = new Set<string>();
              pendingBookingsByLead.set(row.lead_id, held);
            }
            held.add(target.txnId);
          }
        }
      }
    }
    queues.set(row.lead_id, queue);
  }

  return bindings;
}

export function useCalendarEnrichment(
  orgId: string | null,
  rangeStart: Date,
  rangeEnd: Date,
  events: CalendarEvent[],
  _timezone: string,
  refreshKey?: number
): EnrichmentResult {
  const [preCallLeadIds, setPreCallLeadIds] = useState<Set<string>>(new Set());
  const [bindings, setBindings] = useState<Map<string, OutcomeRecord>>(new Map());
  // lead_id -> distinct setter ids who filed a pre-call for that lead.
  const [setterIdsByLead, setSetterIdsByLead] = useState<Map<string, string[]>>(new Map());
  // setter id -> full_name. Source for both the per-event name stamp and the
  // distinct filter checkbox list (preCallSetters).
  const [setterNameById, setSetterNameById] = useState<Map<string, string>>(new Map());
  // user_ids with an ACTIVE organization_members row for this org — the only
  // reps allowed to appear as a filter OPTION (fired/inactive reps are hidden
  // from the checkbox list, but their calls still render, see preCallSetters).
  // null = not loaded yet → fail OPEN (show all) so a slow/failed fetch never
  // hides a working rep.
  const [activeMemberIds, setActiveMemberIds] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const startISO = useMemo(() => rangeStart.toISOString(), [rangeStart.getTime()]);
  const endISO = useMemo(() => rangeEnd.toISOString(), [rangeEnd.getTime()]);

  const extendedStartISO = useMemo(() => {
    const d = new Date(rangeStart);
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  }, [rangeStart.getTime()]);

  // Lead set drives the bindings query. Order-stable join so the cache key
  // doesn't churn when the events array is rebuilt with the same lead_ids.
  const leadIdsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const ev of events) if (ev.lead_id) ids.add(ev.lead_id);
    return Array.from(ids).sort().join(",");
  }, [events]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!supabase || !orgId || events.length === 0) {
      setPreCallLeadIds(new Set());
      setBindings(new Map());
      setSetterIdsByLead(new Map());
      setSetterNameById(new Map());
      return;
    }

    async function fetchEnrichment() {
      const currentLeadCount = leadIdsKey ? leadIdsKey.split(",").length : 0;
      const cacheKey = getEnrichmentCacheKey(orgId!, extendedStartISO, endISO);
      const cached = refreshKey
        ? null
        : getCache<CachedEnrichment>(cacheKey, ENRICHMENT_CACHE_TTL, orgId!);
      // Only serve the cache if it was computed from the SAME lead set we're
      // looking at now. A mid-load partial fetch writes this same range key with
      // fewer leads; without this guard, the full-load read hits that partial
      // entry and shows phantom Missing Outcomes (see CachedEnrichment.leadCount).
      if (cached && Array.isArray(cached.bindings) && cached.leadCount === currentLeadCount) {
        setPreCallLeadIds(new Set(cached.preCallLeadIds));
        setBindings(new Map<string, OutcomeRecord>(cached.bindings));
        setSetterIdsByLead(new Map<string, string[]>(cached.preCallSetterIdsByLead ?? []));
        setSetterNameById(new Map<string, string>(cached.preCallSetterNames ?? []));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const leadIds = leadIdsKey ? leadIdsKey.split(",") : [];
        // Pre-calls: visible-extended-window date filter (cheap, single round trip).
        // Bindings: lead-scoped fetch, but BATCHED in chunks of 100 lead_ids.
        // PostgREST converts `.in('lead_id', [...])` into a query-string IN list;
        // 200+ UUIDs blows past proxy URL limits and the request silently
        // returns an error → empty data → every chip looked uncovered (the
        // 6→63 false-positive on May 8). Batching keeps each request URL
        // under ~4KB. Pagination per batch handles the 1000-row PostgREST cap
        // (PGRST_MAX_ROWS) for any one batch.
        const BATCH_SIZE = 100;
        const PAGE_SIZE = 1000;
        // HTTP/2 multiplexes, so concurrent batches share one connection, but
        // we still cap the fan-out so a 30M-row org with thousands of leads in
        // view can't fire 50 simultaneous round-trips. 6 covers 600 leads per
        // wave — well past any single month grid.
        const MAX_CONCURRENT_BATCHES = 6;

        // One <=100-lead batch, fully paginated. The 100-id slice is a URL-
        // length safety fix (the May 8 6→63 false-positive: a single .in()
        // with 200+ UUIDs blew past the proxy URL limit and silently errored
        // → empty data → every chip looked uncovered). Pagination handles the
        // 1000-row PGRST_MAX_ROWS cap for any one batch.
        async function fetchOneBatch(batch: string[]): Promise<RawTxnRow[]> {
          const rows: RawTxnRow[] = [];
          let from = 0;
          while (true) {
            const { data, error } = await supabase!
              .from("transactions")
              .select(
                // custom_data + created_at feed openPending on pending_close
                // rows. Base-table (not transactions_readable) is this hook's
                // long-standing choice — the RLS calendar carveout
                // (sql/rls_policies.sql:386-396) grants rep roles these rows;
                // do not "fix" to the view. Cache-shape change => /cachebump.
                "id, lead_id, event_type, event_date, call_id, status, cash_collected, call_start_time, custom_data, created_at"
              )
              .eq("organization_id", orgId!)
              .in("lead_id", batch)
              .in("event_type", ["call_booked", "call_cancelled", "sales_call_outcome"])
              .order("event_date", { ascending: true })
              .order("id", { ascending: true })
              .range(from, from + PAGE_SIZE - 1);
            if (error) throw error;
            const page = (data || []) as RawTxnRow[];
            rows.push(...page);
            if (page.length < PAGE_SIZE) break;
            from += PAGE_SIZE;
          }
          return rows;
        }

        // Run the batches CONCURRENTLY (bounded), not in a serial for-loop.
        // The DB does the whole job in ~15ms; the old sequential loop paid
        // N full auth round-trips back-to-back (~5 × ~200ms ≈ 1s) for work
        // that parallelizes to ~one round-trip. computeOutcomeBindings
        // re-sorts every row by (event_date, id) itself, so batch completion
        // order is irrelevant to the binding result. Benchmarked Jun 12 2026.
        async function fetchBindingsRows(): Promise<RawTxnRow[]> {
          const batches: string[][] = [];
          for (let i = 0; i < leadIds.length; i += BATCH_SIZE) {
            batches.push(leadIds.slice(i, i + BATCH_SIZE));
          }
          const all: RawTxnRow[] = [];
          for (let i = 0; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
            const wave = batches.slice(i, i + MAX_CONCURRENT_BATCHES);
            const results = await Promise.all(wave.map(fetchOneBatch));
            for (const r of results) all.push(...r);
          }
          return all;
        }

        // Pre-calls are fetched BY LEAD, never by date window. "Did this lead
        // file a pre-call" is a property of the LEAD, not of the week you happen
        // to be looking at, and windowing it made the answer depend on the view.
        //
        // The old query bounded `call_start_time` to [rangeStart-7d, rangeEnd].
        // A pre-call's `call_start_time` is MIDNIGHT of the call date the setter
        // chose (see /base) and it is NEVER restamped when the call moves. So a
        // lead who reschedules to a different week strands their pre-call
        // outside the window and the chip reads "No pre-call submitted" over a
        // pre-call that plainly exists. Backward moves were partly covered by
        // the -7d extension; forward moves (rebooked EARLIER) had none at all.
        // Live 2026-08-10: Athan (booking Aug 10, pre-call stamped Aug 21) and
        // Shemario Marquise Robson (booking Aug 11, pre-call Aug 19) both read
        // as missing; 63 of 1,027 Rayston bookings since Jun 1 sit >7d from
        // their own pre-call, 8 of them on the uncovered forward side.
        //
        // Same batching discipline as fetchBindingsRows: 100 ids/batch keeps the
        // URL under the PostgREST length ceiling (the May 8 6->63 false-positive
        // was a single unbounded .in()), PAGE_SIZE pagination clears the 1000-row
        // PGRST cap, and .order("id") keeps that pagination deterministic.
        type PreCallRow = {
          lead_id?: string;
          setter_id?: string | null;
          setter?: { full_name?: string | null } | null;
        };
        async function fetchOnePreCallBatch(batch: string[]): Promise<PreCallRow[]> {
          const rows: PreCallRow[] = [];
          let from = 0;
          while (true) {
            const { data, error } = await supabase!
              .from("transactions")
              // setter embed = who filed the pre-call (any role). Proven select
              // shape (PostCallOutcomeModal / useCalendarData / usePreCallCompliance);
              // `setter` is an OBJECT `{ full_name }` or null, never `{full_name:null}`.
              .select("lead_id, setter_id, setter:setter_id ( full_name )")
              .eq("organization_id", orgId!)
              .eq("event_type", "sales_pre_call")
              .in("lead_id", batch)
              .order("id", { ascending: true })
              .range(from, from + PAGE_SIZE - 1);
            if (error) throw error;
            const page = (data || []) as PreCallRow[];
            rows.push(...page);
            if (page.length < PAGE_SIZE) break;
            from += PAGE_SIZE;
          }
          return rows;
        }
        async function fetchPreCallRows(): Promise<PreCallRow[]> {
          if (leadIds.length === 0) return [];
          const batches: string[][] = [];
          for (let i = 0; i < leadIds.length; i += BATCH_SIZE) {
            batches.push(leadIds.slice(i, i + BATCH_SIZE));
          }
          const all: PreCallRow[] = [];
          for (let i = 0; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
            const wave = batches.slice(i, i + MAX_CONCURRENT_BATCHES);
            const results = await Promise.all(wave.map(fetchOnePreCallBatch));
            for (const r of results) all.push(...r);
          }
          return all;
        }

        const [preCallRows, bindingsRows] = await Promise.all([
          fetchPreCallRows(),
          leadIds.length > 0 ? fetchBindingsRows() : Promise.resolve([] as RawTxnRow[]),
        ]);

        if (!isMountedRef.current) return;

        const pcSet = new Set<string>();
        const sIdsByLead = new Map<string, string[]>();
        const sNameById = new Map<string, string>();
        for (const r of preCallRows) {
          if (!r.lead_id) continue;
          pcSet.add(r.lead_id);
          // Only setters with a display name become filter chips. NULL setter_id
          // (pre-call with no attributed setter) or nameless profile → skipped;
          // the lead still counts as having a pre-call (pcSet above).
          const name = r.setter?.full_name ?? null;
          if (!r.setter_id || !name) continue;
          sNameById.set(r.setter_id, name);
          const existing = sIdsByLead.get(r.lead_id);
          if (existing) {
            if (!existing.includes(r.setter_id)) existing.push(r.setter_id);
          } else {
            sIdsByLead.set(r.lead_id, [r.setter_id]);
          }
        }
        setPreCallLeadIds(pcSet);
        setSetterIdsByLead(sIdsByLead);
        setSetterNameById(sNameById);

        const newBindings = computeOutcomeBindings(bindingsRows);
        setBindings(newBindings);

        setCache<CachedEnrichment>(
          cacheKey,
          {
            preCallLeadIds: Array.from(pcSet),
            bindings: Array.from(newBindings.entries()),
            leadCount: leadIds.length,
            preCallSetterIdsByLead: Array.from(sIdsByLead.entries()),
            preCallSetterNames: Array.from(sNameById.entries()),
          },
          orgId!
        );
      } catch (err) {
        logger.error("Failed to fetch calendar enrichment:", err);
        if (isMountedRef.current) setError("Failed to load pre-call and outcome data");
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    }

    fetchEnrichment();
    return () => {
      isMountedRef.current = false;
    };
  }, [orgId, startISO, endISO, extendedStartISO, leadIdsKey, refreshKey]);

  // Active-membership gate for the filter option list. Org-level and cheap, so
  // it runs on its own (independent of the range-keyed enrichment cache). A rep
  // fired mid-week still has pre-calls in range, so without this the fired name
  // stays a checkbox (Alice, live 2026-09-10). Fails OPEN on error: activeMemberIds
  // stays null and preCallSetters shows everyone rather than hiding a live rep.
  useEffect(() => {
    let cancelled = false;
    async function fetchActiveMembers() {
      if (!supabase || !orgId) {
        setActiveMemberIds(null);
        return;
      }
      const { data, error } = await supabase
        .from("organization_members")
        .select("user_id")
        .eq("organization_id", orgId)
        .eq("status", "active");
      if (cancelled) return;
      if (error) {
        logger.error("Failed to fetch active org members for setter filter:", error);
        setActiveMemberIds(null); // fail open
        return;
      }
      setActiveMemberIds(new Set((data ?? []).map((r: { user_id: string }) => r.user_id)));
    }
    fetchActiveMembers();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const now = useMemo(() => new Date(), []);

  const enrichedEvents = useMemo(() => {
    return events.map((ev) => {
      const leadId = ev.lead_id;
      // Pre-call setters are lead-keyed; undefined when the lead filed no
      // pre-call (matches the filter's "no pre-call setter → always passes" rule).
      const setterIds = leadId ? setterIdsByLead.get(leadId) : undefined;
      const setterNames = setterIds
        ? setterIds.map((id) => setterNameById.get(id) ?? id)
        : undefined;
      // Cancels stay otherwise un-enriched, but the pre-call setter is lead-keyed
      // and true regardless of outcome, so it crosses the guard (S1, 2026-08-13).
      // has_pre_call MUST stay unset on cancels — lib/calendarCategory.ts:34-42
      // takes preCallLeadIds as an explicit Set and deliberately never reads it.
      if (ev.event_type === "call_cancelled")
        return { ...ev, pre_call_setter_ids: setterIds, pre_call_setter_names: setterNames };
      const matched = bindings.get(ev.id) ?? null;
      return {
        ...ev,
        has_pre_call: leadId ? preCallLeadIds.has(leadId) : false,
        outcome_status: matched?.status ?? null,
        outcome_cash: matched?.cash ?? null,
        openPending: matched?.openPending,
        pre_call_setter_ids: setterIds,
        pre_call_setter_names: setterNames,
      };
    });
  }, [events, preCallLeadIds, bindings, setterIdsByLead, setterNameById]);

  const missingPreCalls = useMemo(() => {
    return enrichedEvents.filter((ev) => {
      if (ev.event_type === "call_cancelled") return false;
      const callTime = new Date(ev.call_start_time);
      return callTime >= now && ev.has_pre_call === false;
    });
  }, [enrichedEvents, now]);

  const missingOutcomes = useMemo(() => {
    return enrichedEvents.filter((ev) => {
      if (ev.event_type === "call_cancelled") return false;
      const callTime = new Date(ev.call_start_time);
      return callTime < now && (ev.outcome_status === null || ev.outcome_status === undefined);
    });
  }, [enrichedEvents, now]);

  // Distinct setters/dialers who filed a pre-call in range, sorted by name —
  // the checkbox list for the Setters & Dialers sidebar filter. Fired/inactive
  // reps are dropped here (they are not a filterable OPTION) while their calls
  // stay on the grid (pre_call_setter_ids is left intact on every event above,
  // and passesSetterFilter never hides an all-fired-setter call). Mako ruling
  // 2026-09-11: "anyone fired or inactive shouldn't be an option, but their
  // leads should still be there." activeMemberIds null (not loaded / fetch
  // error) → show everyone, so a live rep is never hidden by a slow fetch.
  const preCallSetters = useMemo<PreCallSetter[]>(() => {
    return Array.from(setterNameById.entries())
      .filter(([id]) => !activeMemberIds || activeMemberIds.has(id))
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [setterNameById, activeMemberIds]);

  // preCallLeadIds exposed so callers can answer "did this lead have a pre-call"
  // WITHOUT enriching has_pre_call onto cancelled events (which would activate
  // the has_pre_call-keyed cancel categories and shift DQ'd/Lead-Cancelled counts).
  return {
    enrichedEvents,
    missingPreCalls,
    missingOutcomes,
    preCallLeadIds,
    preCallSetters,
    loading,
    error,
  };
}
